/**
 * The quarterly rates pack (PROD-650): one admin upload of the `BB API` tab's Bloomberg pull per
 * quarter-end, stored once and written into every client's file as `RatesRecast`.
 *
 * Shared by both ends on purpose. The upload screen runs {@link validateEmpyreanRatePack} to show the
 * admin what is wrong before anything is sent, and the API runs the SAME function again as the
 * authority, because nothing a browser sends can be trusted to have been checked.
 */

/** One line of the uploaded `BB API` tab, as read — `pxLast` is still Bloomberg's PERCENT. */
export interface EmpyreanRatePackLineInput {
  /** the worksheet row, for messages only */
  rowNumber?: number | null;
  /** column A, `index`: a curve (`SOFRCV`) or a single-rate series (`PRIME`) */
  seriesMnemonic: string;
  /** column B, `term`, in months — null for a single-rate series; 0 is real (OIS overnight) */
  termMonths: number | null;
  /** column C, `bb code` — null where there is none (COSI is keyed in from a web page) */
  bloombergTicker: string | null;
  /**
   * column D, `px_last` — a PERCENT (`4.13303` is 4.13303%). Whatever the cell held, so a Bloomberg
   * `#N/A Requesting Data...` reaches the message rather than being flattened to blank.
   */
  pxLast: number | string | null;
}

/** One stored point of a pack. */
export interface EmpyreanRatePackPoint {
  /** the pull's own line order — the reference tab and `RatesRecast` render in it */
  sortOrder: number;
  seriesMnemonic: string;
  termMonths: number | null;
  bloombergTicker: string | null;
  /** ⚠ a DECIMAL rate (`0.0413303`), never a percent — the unit every Emport rate is carried in */
  value: number;
}

/** A stored quarter's pack. */
export interface EmpyreanRatePack {
  /** the quarter-end, yyyy-MM-dd */
  asOfDate: string;
  sourceFileName: string;
  uploadedBy: string;
  uploadedDate: Date;
  note: string | null;
  points: EmpyreanRatePackPoint[];
}

/** A pack without its points, for the upload screen's list. */
export interface EmpyreanRatePackSummary {
  asOfDate: string;
  sourceFileName: string;
  uploadedBy: string;
  uploadedDate: Date;
  note: string | null;
  pointCount: number;
  seriesCount: number;
  /** the quarter's investment reinvestment yield, a DECIMAL — null when the pack carries none (PROD-1641) */
  reinvestmentYield: number | null;
}

/**
 * The investment reinvestment yield (PROD-1641): Luke's quarterly number, stored as ONE point of the
 * quarter's pack beside its curves (Mike, 3 Oct 2026) — no term, no ticker, a PERCENT on the way in
 * like every line. The upload screen keys it in, or reads it off a `BB API` line of this name.
 *
 * Never a declared curve or index, so it never reaches `RatesRecast`; it rides on the `BB API` tab as
 * a record of the yield the file used. Generate spreads investments over RRI by it.
 */
export const EMPYREAN_REINVESTMENT_YIELD_SERIES = 'INV_REINVEST_YIELD';

/**
 * The range a reinvestment yield may take, as the PERCENT keyed in. ⚠ A decimal keyed by mistake
 * (`0.0425` for 4.25%) is 100x too small and would import without a murmur, so it is refused by name
 * rather than stored — no investment portfolio reinvests below 0.10%.
 */
export const EMPYREAN_REINVESTMENT_YIELD_MIN_PERCENT = 0.1;
export const EMPYREAN_REINVESTMENT_YIELD_MAX_PERCENT = 20;

export const isReinvestmentYieldSeries = (seriesMnemonic: string | null | undefined): boolean =>
  (seriesMnemonic ?? '').trim().toLowerCase() === EMPYREAN_REINVESTMENT_YIELD_SERIES.toLowerCase();

/** The pack's reinvestment yield, a decimal — null when it has none. */
export const reinvestmentYieldOf = (
  points: readonly Pick<EmpyreanRatePackPoint, 'seriesMnemonic' | 'value'>[]
): number | null => points.find((point) => isReinvestmentYieldSeries(point.seriesMnemonic))?.value ?? null;

export interface EmpyreanRatePackValidation {
  /** the points that would be stored, in the pull's order */
  points: EmpyreanRatePackPoint[];
  /** refusals — any one refuses the whole upload */
  errors: string[];
  /** accepted, but worth telling the uploader: a placeholder line skipped, an identical repeat collapsed */
  notes: string[];
}

/** The widths of the columns these land in (`emp-rate-packs.sql`); longer would fail the insert. */
export const EMPYREAN_RATE_PACK_MNEMONIC_MAX_LENGTH = 50;
export const EMPYREAN_RATE_PACK_TICKER_MAX_LENGTH = 50;

/** yyyy-MM-dd that is the last day of March, June, September or December. */
export const isQuarterEndDate = (isoDate: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
  if (!match) {
    return false;
  }
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (![3, 6, 9, 12].includes(month)) {
    return false;
  }
  // day 0 of the next month is the last day of this one
  return day === new Date(Date.UTC(year, month, 0)).getUTCDate();
};

/**
 * Bloomberg's percent as the decimal Emport stores. Rounded to 12 significant figures so the float
 * noise of the division (`3.39598 / 100` is `0.03395980000000001`) is not what gets stored — the
 * pull itself carries at most six.
 */
export const percentToDecimalRate = (percent: number): number => Number((percent / 100).toPrecision(12));

/** The decimal back as Bloomberg's percent, for the reference tab's `px_last` column. */
export const decimalRateToPercent = (rate: number): number => Number((rate * 100).toPrecision(12));

const describeLine = (line: EmpyreanRatePackLineInput): string => {
  const where = line.rowNumber ? `row ${line.rowNumber}: ` : '';
  const term = line.termMonths === null || line.termMonths === undefined ? '' : ` ${line.termMonths}M`;
  const ticker = line.bloombergTicker ? ` (${line.bloombergTicker})` : '';
  return `${where}${line.seriesMnemonic}${term}${ticker}`;
};

const readPercent = (pxLast: number | string | null): number | null | 'blank' => {
  if (pxLast === null || pxLast === undefined) {
    return 'blank';
  }
  if (typeof pxLast === 'number') {
    return Number.isFinite(pxLast) ? pxLast : null;
  }
  const trimmed = pxLast.trim();
  if (!trimmed) {
    return 'blank';
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Checks an upload and turns it into the points to store. Every line is checked before any answer,
 * so the uploader sees every problem at once rather than one per attempt.
 *
 * Refused:
 * - an as-of that is not a quarter-end — a pack is looked up by the client's as-of quarter, so a
 *   mid-quarter date could never be found;
 * - a line with a ticker and no number — that is the refresh not having run (Bloomberg's `#N/A`),
 *   and a curve with a hole in it is not a curve;
 * - a term that is not a whole number of months, or is negative;
 * - two lines for one (series, term) that DISAGREE — there is no right answer to pick;
 * - a pull with nothing in it.
 *
 * Accepted with a note:
 * - a line with neither a ticker nor a number is a placeholder in the template (Luke's Boston 360M)
 *   and is skipped;
 * - two lines for one (series, term) with the same ticker AND the same number collapse to one —
 *   Luke's own pull lists `11COF` twice, identically, and refusing it would refuse his file.
 *
 * `px_last` is a PERCENT and is stored as a decimal: `4.13303` becomes `0.0413303`.
 */
export const validateEmpyreanRatePack = (
  asOfDate: string,
  lines: readonly EmpyreanRatePackLineInput[]
): EmpyreanRatePackValidation => {
  const errors: string[] = [];
  const notes: string[] = [];
  if (!isQuarterEndDate(asOfDate)) {
    errors.push(`The pull's date must be a quarter-end (Mar 31, Jun 30, Sep 30 or Dec 31), not "${asOfDate}".`);
  }

  const points: EmpyreanRatePackPoint[] = [];
  const seen = new Map<string, EmpyreanRatePackPoint>();
  lines.forEach((raw) => {
    const line: EmpyreanRatePackLineInput = {
      ...raw,
      seriesMnemonic: (raw.seriesMnemonic ?? '').trim(),
      bloombergTicker: raw.bloombergTicker?.trim() || null
    };
    if (!line.seriesMnemonic) {
      return;
    }
    const label = describeLine(line);
    const percent = readPercent(line.pxLast);
    if (percent === 'blank') {
      if (line.bloombergTicker) {
        errors.push(`${label} has a ticker but no number — was the Bloomberg refresh run?`);
      } else {
        notes.push(`${label} has no ticker and no number, so it was skipped.`);
      }
      return;
    }
    if (percent === null) {
      errors.push(`${label}: px_last is "${String(line.pxLast)}", not a number.`);
      return;
    }
    const term = line.termMonths;
    if (term !== null && term !== undefined && (!Number.isInteger(term) || term < 0)) {
      errors.push(`${label}: the term must be a whole number of months, not ${term}.`);
      return;
    }
    if (line.seriesMnemonic.length > EMPYREAN_RATE_PACK_MNEMONIC_MAX_LENGTH) {
      errors.push(`${label}: the index name is longer than ${EMPYREAN_RATE_PACK_MNEMONIC_MAX_LENGTH} characters.`);
      return;
    }
    if ((line.bloombergTicker?.length ?? 0) > EMPYREAN_RATE_PACK_TICKER_MAX_LENGTH) {
      errors.push(`${label}: the ticker is longer than ${EMPYREAN_RATE_PACK_TICKER_MAX_LENGTH} characters.`);
      return;
    }
    if (isReinvestmentYieldSeries(line.seriesMnemonic)) {
      if (term !== null && term !== undefined) {
        errors.push(`${label}: the investment reinvestment yield is one number, with no term.`);
        return;
      }
      if (percent < EMPYREAN_REINVESTMENT_YIELD_MIN_PERCENT || percent >= EMPYREAN_REINVESTMENT_YIELD_MAX_PERCENT) {
        errors.push(
          `${label}: an investment reinvestment yield of ${percent}% isn't plausible — enter it as a percent (4.25 for 4.25%), ` +
            `at least ${EMPYREAN_REINVESTMENT_YIELD_MIN_PERCENT}% and below ${EMPYREAN_REINVESTMENT_YIELD_MAX_PERCENT}%.`
        );
        return;
      }
    }

    const point: EmpyreanRatePackPoint = {
      sortOrder: points.length,
      seriesMnemonic: line.seriesMnemonic,
      termMonths: term ?? null,
      bloombergTicker: line.bloombergTicker,
      value: percentToDecimalRate(percent)
    };
    // case-insensitive, as the table's collation and Empyrean's mnemonic match both are
    const key = `${point.seriesMnemonic.toLowerCase()}\u0000${point.termMonths ?? ''}`;
    const earlier = seen.get(key);
    if (earlier) {
      if (earlier.value === point.value && (earlier.bloombergTicker ?? '') === (point.bloombergTicker ?? '')) {
        notes.push(`${label} repeats an earlier line exactly, so it is stored once.`);
      } else {
        errors.push(`${label} appears twice with different values — which one is right?`);
      }
      return;
    }
    seen.set(key, point);
    points.push(point);
  });

  if (!points.length && !errors.length) {
    errors.push('The pull has no rates in it.');
  }
  return { points, errors, notes };
};
